<template>
    <div class="website-settings-v3 ppv2-relative">
        <!-- Main Content Card -->
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-3xl ppv2-p-6 ppv2-shadow-lg">
            <!-- Header with Title and Quick Actions -->
            <div class="ppv2-flex ppv2-flex-wrap ppv2-justify-between ppv2-items-center ppv2-gap-3">
                <Header :title="$t('website_settings.title')" />
                <div class="ppv2-flex ppv2-mb-4" :style="{ gap: normalizeSize(16, true) }">
                    <BaseButton variant="outline" @click="goToWidget">
                        {{ $t('website_settings.actions.integrate_widget') }}
                        <CodeXml :size="18" class="ppv2-ml-2" />
                    </BaseButton>
                    <BaseButton variant="outline" @click="viewSite">
                        {{ $t('website_settings.actions.view_site') }}
                        <ExternalLink :size="18" class="ppv2-ml-2" />
                    </BaseButton>
                </div>
            </div>
            <!-- Horizontal Tab Navigation -->
            <div class="ppv2-border-b ppv2-border-gray-200 dark:ppv2-border-gray-700 ppv2-mb-6">
                <nav class="ppv2--mb-px ppv2-flex ppv2-space-x-8 ppv2-overflow-x-auto ppv2-scrollbar-hide" aria-label="Tabs">
                    <a
                        v-for="tab in tabs"
                        :key="tab.id"
                        href="javascript:void(0)"
                        @click="activeTab = tab.id"
                        :class="[
                            activeTab === tab.id
                                ? 'ppv2-border-primary-500 poppinsBold'
                                : 'ppv2-border-transparent poppinsMedium',
                            'ppv2-whitespace-nowrap ppv2-px-1 ppv2-border-b-2 ppv2-text-black hover:ppv2-text-black ppv2-transition-colors ppv2-duration-200',
                        ]"
                        :style="{
                            fontSize: normalizeFontSize(14),
                            paddingTop: normalizeSizeVertical(16, true),
                            paddingBottom: normalizeSizeVertical(16, true),
                        }"
                    >
                        {{ tab.label }}
                    </a>
                </nav>
            </div>

            <!-- Tab Content -->
            <div class="ppv2-mt-6">
                <!-- Loading State -->
                <div v-if="loading" class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-py-20">
                    <div
                        class="ppv2-animate-spin ppv2-rounded-full ppv2-h-10 ppv2-w-10 ppv2-border-4 ppv2-border-primary-600 ppv2-border-t-transparent"
                    ></div>
                </div>

                <!-- Dynamic Component -->
                <div v-else class="ppv2-animate-fadeIn">
                    <keep-alive>
                        <component
                            :is="currentTabComponent"
                            :data="data"
                            @save="handleSave"
                            @refresh="$emit('refresh')"
                            :plan-publish-form-request-quotation="planPublishFormRequestQuotation"
                            :tenant="tenant"
                        ></component>
                    </keep-alive>
                </div>
            </div>
        </div>

        <!-- Floating Save Button -->
        <div class="save-button-floating">
            <BaseButton
                :loading="saving"
                @click="triggerTabSave"
                variant="primary"
                customClass="!ppv2-rounded-xl !ppv2-px-6 !ppv2-py-3 ppv2-shadow-lg"
            >
                {{ saving ? $t('common.saving') : $t('common.save') }}
            </BaseButton>
        </div>
    </div>
</template>

<script>
import TabGeneral from './components/TabGeneral.vue'
import TabSections from './components/TabSections.vue'
import TabAbout from './components/TabAbout.vue'
import TabFaq from './components/TabFaq.vue'
import TabCguv from './components/TabCguv.vue'
import TabFooter from './components/TabFooter.vue'
import TabOutils from './components/TabOutils.vue'
import TabMenu from './components/TabMenu.vue'
import TabSeo from './components/TabSeo.vue'
import TabMobileIntro from './components/TabMobileIntro.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import { CodeXml, ExternalLink } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import Header from '../../../Shared/ConfigurationHeader.vue'

export default {
    name: 'WebsiteSettingsV3Index',
    components: {
        TabGeneral,
        TabSections,
        TabAbout,
        TabFaq,
        TabCguv,
        TabFooter,
        TabOutils,
        TabMenu,
        TabSeo,
        TabMobileIntro,
        BaseButton,
        CodeXml,
        ExternalLink,
        Header,
    },
    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
        planPublishFormRequestQuotation: {
            type: Object,
            default: () => ({}),
        },
        tenant: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            activeTab: 'general',
            tabs: [
                { id: 'general', label: this.$t('website_settings.tabs.general'), component: 'TabGeneral' },
                { id: 'pages', label: this.$t('website_settings.tabs.pages'), component: 'TabAbout' },
                { id: 'menu', label: this.$t('website_settings.tabs.menu'), component: 'TabMenu' },
                { id: 'sections', label: this.$t('website_settings.tabs.sections'), component: 'TabSections' },
                { id: 'faq', label: this.$t('website_settings.tabs.faq'), component: 'TabFaq' },
                { id: 'cguv', label: this.$t('website_settings.tabs.cguv'), component: 'TabCguv' },
                { id: 'footer', label: this.$t('website_settings.tabs.footer'), component: 'TabFooter' },
                { id: 'seo', label: this.$t('website_settings.tabs.seo'), component: 'TabSeo' },
                // { id: 'mobile_intro', label: this.$t('website_settings.tabs.mobile_intro'), component: 'TabMobileIntro' },
                // { id: 'outils', label: this.$t('website_settings.tabs.outils'), component: 'TabOutils' },
            ],
        }
    },
    computed: {
        currentTabComponent() {
            const tab = this.tabs.find((t) => t.id === this.activeTab)
            return tab ? tab.component : 'TabGeneral'
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        handleSave(payload) {
            console.log('handleSave')
            console.log(payload)
            this.saving = payload?.loading || false
            if (payload?.success) {
                this.$toast.success(payload.message || this.$t('website_settings.actions.save_success'))
            }
        },
        triggerTabSave() {
            this.$root.$emit('website-settings-trigger-save-' + this.activeTab)
        },
        goToWidget() {
            window.location.href = '/back-office/v3/widgets'
        },
        viewSite() {
            let url = this.data?.domain || window.location.href
            // Remove /back-office and everything after if present
            url = url.split('/back-office')[0]
            // Remove -bo from subdomain
            url = url.replace('-bo.', '.')
            window.open(url, '_blank')
        },
    },
    mounted() {
        // Optional: Ensure main container background is handled if necessary for the layout
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.add('website-settings-v3-no-bg')
        }
    },
    beforeDestroy() {
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
            mainContainer.classList.remove('website-settings-v3-no-bg')
        }
    },
}
</script>

<style scoped>
@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.ppv2-animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.ppv2-scrollbar-hide::-webkit-scrollbar {
    display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.ppv2-scrollbar-hide {
    -ms-overflow-style: none;
    /* IE and Edge */
    scrollbar-width: none;
    /* Firefox */
}

.save-button-floating {
    position: fixed;
    right: 40px;
    bottom: 40px;
    z-index: 1000;
}

@media (max-width: 1024px) {
    .save-button-floating {
        right: 20px;
        bottom: 20px;
    }
}
</style>

<style>
/* Global overrides for this page context */
#main-container.website-settings-v3-no-bg {
    background: transparent !important;
    box-shadow: none !important;
}
</style>
